/* eslint-disable @typescript-eslint/no-explicit-any */

import _ from 'lodash'
import { GetDeviceDataQuerySchema, GetDeviceUuidsQuerySchema } from '../../DeviceConfig/device_schemas'
import { GetStationByDeviceIdQuerySchema } from '../../DeviceConfig/station_schemas'
import {
    GetCoordinateParkingDetectionsQuerySchema,
    GetDeviceSystemImageQuerySchema,
    GetDeviceSystemImageVariablesSchema,
    GetStationDataByNameQuerySchema,
    GetStationNamesQuerySchema,
    MutationRootUpdateStationDataMutationArgsSchema,
    TriggerScsFetchOccupancyMutationSchema,
    TriggerScsFetchOccupancyMutationVariablesSchema,
    UpdateStationConfigMutationSchema,
    UpdateStationConfigMutationVariablesSchema,
    UpdateStationDataMutationSchema,
    UpdateStationEnabledMutationSchema,
    UpdateStationEnabledMutationVariablesSchema,
    type GetStationDataByNameQuery,
    type MutationRootUpdateStationDataMutationArgs,
    type TriggerScsFetchOccupancyMutationVariables,
    type UpdateStationConfigMutationVariables,
    type UpdateStationEnabledMutationVariables
} from '../station_schemas'
import { ToastTypes, launchToast } from '../../../components'
import { useMemo } from 'react'
import { z } from 'zod'
import { useApiMutation, useDataFetcher } from '../../../utils'
import { useApi } from '../../../utils/useApi'
import { LotStationType } from '../../Lot/lot-types'
import { StationName } from '../station_types'
import { ODDisplayOverrideConfig } from '../ODDisplayOverride'

export const useSaveStationConfigFilter = (
    refreshSelectedStationData: any,
    selectedStation: StationName | null | undefined
) => {
    const { mutate: editStationConfig, isPending: loadingSaveStationConfigFilter } = useApiMutation<
        UpdateStationConfigMutationVariables,
        typeof UpdateStationConfigMutationSchema
    >('/platform/device/update_station_config', UpdateStationConfigMutationSchema, {
        onError: (error) => {
            launchToast(ToastTypes.Error, 'Error: Could not save configuration. Check the console for more details!')
            console.error(error)
        },
        onSuccess: () => {
            refreshSelectedStationData({
                station_name: selectedStation?.station_name
            })
            launchToast(ToastTypes.Success, 'Successfully updated configuration!')
        }
    })

    const saveStationConfigFilter = (
        newFilterData: any,
        selectedStation: StationName | null | undefined,
        selectedStationData: GetStationDataByNameQuery | undefined
    ) => {
        let currentConfig = _.cloneDeep(selectedStationData?.station[0]?.config)
        const newFilterConfig = {
            filters: newFilterData
        }
        if (!currentConfig) {
            currentConfig = {
                filters: []
            }
        }
        if (currentConfig.autowhitelist) {
            currentConfig.autowhitelist = { ...currentConfig.autowhitelist }
        }
        if (newFilterData) {
            const updatedFilters = newFilterConfig.filters.map((filter: any) => {
                return {
                    ...filter,
                    priority: Number(filter.priority)
                }
            })
            currentConfig.filters = updatedFilters
        }

        const variables = UpdateStationConfigMutationVariablesSchema.parse({
            station_name: selectedStation?.station_name,
            config: currentConfig,
            timestamp: selectedStationData?.station[0].config_timestamp
        })
        editStationConfig(variables)
    }

    return { saveStationConfigFilter, loadingSaveStationConfigFilter }
}

export const useSaveStationConfig = (
    refreshSelectedStationData?: any,
    selectedStation?: StationName | null | undefined,
    launchConfirmToast = true
) => {
    const { mutateAsync: updateStationConfig, isPending: loadingSaveStationConfig } = useApiMutation<
        UpdateStationConfigMutationVariables,
        typeof UpdateStationConfigMutationSchema
    >('/platform/device/update_station_config', UpdateStationConfigMutationSchema)

    const saveStationConfig = async (
        enabledAutoWhitelisting: boolean,
        numberOfDays: number | null,
        selectedStation: StationName | null | undefined,
        selectedStationData: LotStationType | undefined,
        callback?: () => void
    ) => {
        let currentConfig = _.cloneDeep(selectedStationData?.config)
        const newAutoWhiteListConfig = {
            duration_days: numberOfDays,
            enable: enabledAutoWhitelisting
        }
        if (!currentConfig) {
            currentConfig = {
                filters: []
            }
        }
        if (currentConfig.autowhitelist) {
            currentConfig.autowhitelist = {
                ...currentConfig.autowhitelist,
                ...newAutoWhiteListConfig
            }
        } else {
            currentConfig.autowhitelist = { ...newAutoWhiteListConfig }
        }

        try {
            const variables = UpdateStationConfigMutationVariablesSchema.parse({
                station_name: selectedStation?.station_name,
                config: currentConfig,
                timestamp: (selectedStationData as any)?.config_timestamp
            })
            await updateStationConfig(variables)
            refreshSelectedStationData?.({
                station_name: selectedStation?.station_name
            })
            if (launchConfirmToast) {
                launchToast(ToastTypes.Success, 'Successfully updated configuration!')
            }
            callback?.()
        } catch (error) {
            launchToast(ToastTypes.Error, 'Error: Could not save configuration. Check the console for more details !')
            console.error(error)
        }
    }

    return { saveStationConfig, loadingSaveStationConfig }
}

export const useSaveODDisplayOverride = (
    refreshSelectedStationData: any,
    selectedStation: StationName | null | undefined
) => {
    const { mutate: editStationConfig, isPending: loadingSaveODDisplayOverride } = useApiMutation<
        UpdateStationConfigMutationVariables,
        typeof UpdateStationConfigMutationSchema
    >('/platform/device/update_station_config', UpdateStationConfigMutationSchema, {
        onError: (error) => {
            launchToast(ToastTypes.Error, 'Error: Could not save configuration. Check the console for more details !')
            console.error(error)
        },
        onSuccess: () => {
            refreshSelectedStationData({
                station_name: selectedStation?.station_name
            })
            launchToast(ToastTypes.Success, 'Successfully updated configuration!')
        }
    })

    const { mutateAsync: triggerScsFetchOccupancyMutation, data: triggerData } = useApiMutation<
        TriggerScsFetchOccupancyMutationVariables,
        typeof TriggerScsFetchOccupancyMutationSchema
    >('/occupancy/scs_fetch_occupancy', TriggerScsFetchOccupancyMutationSchema)

    const TriggerScsFetchOccupancy = (input: TriggerScsFetchOccupancyMutationVariables) => {
        const variables = TriggerScsFetchOccupancyMutationVariablesSchema.parse(input)
        return triggerScsFetchOccupancyMutation(variables)
    }

    const saveODDisplayOverride = (
        newODConfig: ODDisplayOverrideConfig,
        selectedStation: StationName | null | undefined,
        selectedStationData: GetStationDataByNameQuery | undefined
    ) => {
        let currentConfig = _.cloneDeep(selectedStationData?.station[0]?.config)

        if (!currentConfig) {
            currentConfig = { ...newODConfig }
        } else {
            currentConfig = { ...currentConfig, ...newODConfig }
        }
        const variables = UpdateStationConfigMutationVariablesSchema.parse({
            station_name: selectedStation?.station_name,
            config: currentConfig,
            timestamp: selectedStationData?.station[0].config_timestamp
        })
        editStationConfig(variables)
    }

    return {
        saveODDisplayOverride,
        loadingSaveODDisplayOverride,
        TriggerScsFetchOccupancy,
        triggerData
    }
}

export const useGetStationNames = () => {
    const result = useDataFetcher('get_station_names', {}, GetStationNamesQuerySchema, {
        queryKey: ['data_fetcher', 'get_station_names']
    })
    return {
        data: result.data,
        refreshStationNames: result.refetch,
        loadingFetchStationNames: result.isFetching
    }
}

export const useGetDeviceUuids = () => {
    const result = useDataFetcher('get_device_uuids', {}, GetDeviceUuidsQuerySchema, {
        queryKey: ['data_fetcher', 'get_device_uuids']
    })
    return { data: result.data, refreshDevices: result.refetch, loading: result.isFetching }
}

export const useGetStationDataByName = (stationName: string | undefined) => {
    const result = useDataFetcher(
        'get_station_data_by_name',
        { station_name: stationName },
        GetStationDataByNameQuerySchema,
        {
            queryKey: ['data_fetcher', 'get_station_data_by_name', stationName],
            enabled: !!stationName
        }
    )
    return { data: result.data, refetch: result.refetch, loading: result.isFetching }
}

export const useGetStationByDeviceId = (deviceUUID: string | undefined | null) => {
    const result = useDataFetcher(
        'get_station_by_device_id',
        { device_uuid: deviceUUID },
        GetStationByDeviceIdQuerySchema,
        {
            queryKey: ['data_fetcher', 'get_station_by_device_id', deviceUUID],
            enabled: !!deviceUUID
        }
    )
    return { data: result.data, refetchDeviceStations: result.refetch }
}

export const useGetDeviceData = (deviceUUID: string | undefined | null) => {
    const result = useDataFetcher('get_device_data', { device_uuid: deviceUUID }, GetDeviceDataQuerySchema, {
        queryKey: ['data_fetcher', 'get_device_data', deviceUUID],
        enabled: !!deviceUUID
    })
    return { data: result.data, refetch: result.refetch, loading: result.isFetching }
}

export const useSaveStation = (refreshSelectedStationData?: any, selectedStation?: StationName | null | undefined) => {
    const { mutate: editStation, isPending: loadingSaveStation } = useApiMutation<
        MutationRootUpdateStationDataMutationArgs,
        typeof UpdateStationDataMutationSchema
    >('/platform/device/update_station_data', UpdateStationDataMutationSchema, {
        onError: (error) => {
            launchToast(ToastTypes.Error, error.message)
            console.error(error)
        },
        onSuccess: () => {
            refreshSelectedStationData?.({
                station_name: selectedStation?.station_name
            })
            launchToast(ToastTypes.Success, 'Successfully updated the station!')
        }
    })

    const saveStation = (
        device_direction: string,
        selectedStation: StationName | null | undefined,
        lot_section_id: string | null | undefined,
        device_uuid?: string | undefined | null,
        callback?: () => void,
        customSuccessMessage?: string
    ) => {
        if (!selectedStation?.station_name) {
            launchToast(ToastTypes.Error, 'No station selected')
            return
        }
        const variables = MutationRootUpdateStationDataMutationArgsSchema.parse({
            station_name: selectedStation.station_name,
            device_uuid,
            device_direction,
            lot_section_id
        })
        editStation(variables, {
            onSuccess: () => {
                callback?.()
                refreshSelectedStationData?.({
                    station_name: selectedStation?.station_name
                })
                launchToast(ToastTypes.Success, customSuccessMessage || 'Successfully updated the station!')
            }
        })
    }

    return { saveStation, loadingSaveStation }
}

export const useUpdateStationEnabled = (
    refreshSelectedStationData?: any,
    selectedStation?: StationName | null | undefined
) => {
    const { mutate: updateStationEnabled, isPending: loadingUpdateStationEnabled } = useApiMutation<
        UpdateStationEnabledMutationVariables,
        typeof UpdateStationEnabledMutationSchema
    >('/platform/device/update_station_enabled', UpdateStationEnabledMutationSchema, {
        onError: (error) => {
            launchToast(ToastTypes.Error, `Error: Could not update the station. ${error.message}`)
            console.error(error)
        },
        onSuccess: (data) => {
            refreshSelectedStationData?.({
                station_name: selectedStation?.station_name
            })
            launchToast(ToastTypes.Success, data.message)
        }
    })

    const setStationEnabled = (enabled: boolean) => {
        if (!selectedStation?.station_name) {
            launchToast(ToastTypes.Error, 'No station selected')
            return
        }
        const variables = UpdateStationEnabledMutationVariablesSchema.parse({
            station_name: selectedStation.station_name,
            enabled
        })
        updateStationEnabled(variables)
    }

    return { setStationEnabled, loadingUpdateStationEnabled }
}

export const useDeleteStation = (callback: () => void) => {
    const { mutate: deleteStation, isPending: isDeletingStation } = useApiMutation<
        { station_name: string },
        z.ZodObject<{ message: z.ZodString }>
    >('/platform/device/delete_station', z.object({ message: z.string() }), {
        onError: (error) => {
            launchToast(ToastTypes.Error, `Error: Could not delete the station. ${error.message}`)
            console.error(error)
        },
        onSuccess: (data) => {
            callback()
            launchToast(ToastTypes.Success, data.message)
        }
    })

    return { execute: (stationName: string) => deleteStation({ station_name: stationName }), isDeletingStation }
}

export const useGetSystemImageQuery = (device_uuid: string | undefined | null, latest: boolean | undefined | null) => {
    const variables = useMemo(
        () =>
            device_uuid
                ? GetDeviceSystemImageVariablesSchema.parse({
                      device_uuid,
                      latest: latest || false
                  })
                : undefined,
        [device_uuid, latest]
    )
    const result = useApi('/image-request/get_system_image', variables, GetDeviceSystemImageQuerySchema, {
        queryKey: ['image-request/get_system_image', variables],
        enabled: !!variables
    })

    return { data: result.data, loadingFetchSystemImage: result.isFetching, error: result.error }
}

export const useGetCoordinatesParkingDetections = (
    timestamp_value: string | undefined,
    uuid: string | undefined,
    direction: string | undefined,
    missing_lp_value: boolean | undefined,
    lot_id: string | undefined
) => {
    const enabled =
        timestamp_value !== undefined &&
        uuid !== undefined &&
        direction !== undefined &&
        missing_lp_value !== undefined &&
        lot_id !== undefined

    const result = useDataFetcher(
        'get_coordinate_parking_detections',
        {
            device_uuid: uuid,
            direction,
            missing_lp: missing_lp_value,
            timestamp: timestamp_value,
            limit: 30,
            parking_lot: lot_id
        },
        GetCoordinateParkingDetectionsQuerySchema,
        {
            queryKey: [
                'data_fetcher',
                'get_coordinate_parking_detections',
                uuid,
                direction,
                missing_lp_value,
                timestamp_value,
                lot_id
            ],
            enabled,
            staleTime: 0,
            cacheTime: 0
        }
    )

    return { data: result.data, loading: result.isFetching, error: result.error }
}
